import React, { useEffect, useMemo, useState } from "react";
import { FaCalendarAlt, FaUser, FaClock, FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";

const THEME = {
  shell: "bg-gradient-to-br from-[#180303] via-[#2a0707] to-[#0f0202]",
  frame: "shadow-[0_0_90px_rgba(255,59,59,0.18)]",
  accentBar: "bg-[linear-gradient(90deg,#ff1f1f_0%,#ff3b3b_45%,#b30000_100%)]",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
}

export default function BlogPost() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const paragraphs = useMemo(() => {
    if (!post?.content) return [];
    return post.content
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);
  }, [post]);

  useEffect(() => {
    const fetchPost = async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (error) {
        console.error("Failed to fetch blog post:", error.message);
        setPost(null);
        setLoading(false);
        return;
      }

      setPost(data);
      setLoading(false);
    };

    fetchPost();
  }, [slug]);

  useEffect(() => {
    document.title = post?.title
      ? `${post.title} • Light Idea Productions`
      : "Blog Post • Light Idea Productions";
  }, [post]);

  const blockContextMenu = (e) => e.preventDefault();
  const blockDrag = (e) => e.preventDefault();

  const blockCopyShortcuts = (e) => {
    const key = e.key.toLowerCase();

    if (
      (e.ctrlKey || e.metaKey) &&
      (key === "c" || key === "x" || key === "a" || key === "s" || key === "p")
    ) {
      e.preventDefault();
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${THEME.shell} text-white`}>
        <div className="mx-auto w-full max-w-[1200px] px-4 py-8">
          <div className="rounded-[30px] border border-white/10 bg-black/35 px-6 py-10">
            Loading blog post...
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className={`min-h-screen ${THEME.shell} text-white`}>
        <div className="mx-auto w-full max-w-[1200px] px-4 py-8">
          <div className="rounded-[30px] border border-white/10 bg-black/35 px-6 py-10">
            Blog post not found.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${THEME.shell} text-white select-none`}
      onContextMenu={blockContextMenu}
      onDragStart={blockDrag}
      onKeyDown={blockCopyShortcuts}
    >
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <article
          className={`relative overflow-hidden rounded-[30px] border border-white/10 bg-black/35 ${THEME.frame} select-none`}
          draggable={false}
        >
          <div className={`h-1.5 w-full ${THEME.accentBar}`} />

          <div className="pointer-events-none absolute inset-0 opacity-[0.05]">
            <div className="flex h-full w-full items-center justify-center text-4xl font-bold uppercase tracking-[0.35em] text-white">
              Light Idea Productions
            </div>
          </div>

          <div className="relative px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
            <div className="flex items-start justify-between gap-4">
              <button
                type="button"
                onClick={() => navigate("/blogs")}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/12"
              >
                <FaArrowLeft className="h-3 w-3" />
                Back to Blogs
              </button>

              <div className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-white/75">
                {post.category}
              </div>
            </div>

            <h1 className="mt-6 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              {post.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-white/60 sm:text-sm">
              <span className="inline-flex items-center gap-2">
                <FaUser className="h-3 w-3" />
                {post.author_name}
              </span>
              <span className="inline-flex items-center gap-2">
                <FaCalendarAlt className="h-3 w-3" />
                {formatDate(post.published_date)}
              </span>
              <span className="inline-flex items-center gap-2">
                <FaClock className="h-3 w-3" />
                {post.read_time_minutes} min read
              </span>
            </div>

            <div className="mt-10 space-y-6 text-sm leading-8 text-white/80 sm:text-base select-none">
              {paragraphs.map((paragraph, index) => (
                <p key={index} draggable={false}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
